import { BuilderComponent } from "@builder.io/react";
import { Footer } from "@faire/retailer-visitor-shared/components/Footer/index";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { getBuilderAPIKey } from "@faire/web--source/builder/BuilderComponents/builderUtils";
import { initializeBuilder } from "@faire/web--source/builder/BuilderComponents/initializeBuilder";
import { useBuilderContentQuery } from "@faire/web--source/builder/BuilderComponents/useBuilderContentQuery";
import { THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES } from "@faire/web--source/common/consts/THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES";
import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { LoadingSkeleton } from "@faire/web--source/slate/LoadingSkeleton";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useMergedRefs } from "@faire/web--source/ui/hooks/useMergedRefs";
import { trackRetailerMarketingBuilderMarketingLandingPageView } from "@faire/web-api--source/events/retailerMarketing/view/builderMarketingLandingPage";
import { usePathname } from "next/navigation";
import React, { Suspense, useEffect } from "react";

const BUILDER_LANDING_PAGE_MODEL_NAME = "landing-page";
const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();

export const BuilderMarketingLandingPage: React.FC = () => {
  useEffect(() => {
    const apiKey = getBuilderAPIKey();
    if (apiKey) {
      initializeBuilder(apiKey);
    }
  }, []);

  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { retailerToken } = useRetailer();
  const userCountry = getSessionCountry();
  const isEurope = THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES.includes(
    userCountry as (typeof THREE_LETTER_EUROPE_SUPPORTED_COUNTRIES)[number]
  );
  const pathname = usePathname();

  const {
    data: builderContent,
    isFetching,
    dataUpdatedAt,
  } = useBuilderContentQuery(BUILDER_LANDING_PAGE_MODEL_NAME, {
    userAttributes: { isLoggedInRetailer, isEurope },
  });

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "load-builder-component",
    pageLoadKey: "builder-marketing-landing-page",
    dataUpdatedAt,
    numComponents: 1,
    enableTrackPerformanceBreakdown: "internal-tracking",
    isPageLoading: isFetching,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
  });
  const viewRef = useDoActionWhenVisible(() => {
    trackRetailerMarketingBuilderMarketingLandingPageView(
      pathname,
      retailerToken ?? ""
    );
  });
  const ref = useMergedRefs(loadTimingRef, viewRef);
  if (isFetching) {
    return <LoadingSkeleton aspectRatio={1} width="100%" />;
  }

  return (
    <div ref={ref}>
      <Suspense fallback={null}>
        <BuilderComponent
          model={BUILDER_LANDING_PAGE_MODEL_NAME}
          content={builderContent}
          data={{ retailerToken: retailerToken ?? "" }}
        />
      </Suspense>
      <Footer />
    </div>
  );
};
